import { UserPlus, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/use-toast";
import { useProfile } from "@/hooks/useProfile";
import useReferralStats from "@/hooks/useReferralStats";
import { inviteFriendToSignUp } from "@/lib/invitation";
import { capture as posthogCapture } from "@/services/posthog";
import { useCallback } from "react";

const ReferFriendCard = () => {
  const { data: userProfile, isLoading: isProfileLoading } = useProfile();
  const { data: stats, isLoading: isStatsLoading } = useReferralStats();

  const isGuest = !userProfile && !isProfileLoading;
  const referralCount = stats?.referralCount || 0;

  const handleShare = useCallback(() => {
    if (isGuest) {
      toast({
        title: "Please sign in",
        description: "You need to be signed in to refer friends.",
      });
      return;
    }

    const referralCode = userProfile?.referral_code;
    try {
      if (!referralCode) {
        throw new Error("Referral code not found");
      }

      posthogCapture("referral_shared", { surface: "refer_friend_card" });
      inviteFriendToSignUp(referralCode);
    } catch (error) {
      console.error("Error creating referral:", error);
      toast({
        title: "Error",
        description: "Failed to create sharing link. Please try again.",
        variant: "destructive",
      });
    }
  }, [userProfile?.referral_code, isGuest]);

  return (
    <div className="bg-card rounded-lg p-4 shadow-md">
      <div className="flex items-center mb-2">
        <UserPlus className="text-secondary mr-2" />
        <h2 className="text-lg font-semibold text-card-foreground">Refer a Friend</h2>
      </div>
      <p className="text-muted-foreground mb-3">
        Share Nimi with friends and earn £5 in credits for each friend who joins!
      </p>
      
      {!isGuest && !isStatsLoading && (
        <p className="text-sm text-foreground mb-3">
          {referralCount === 0 ? (
            <span>You haven't referred anyone yet</span>
          ) : (
            <span className="font-medium">
              🎉 You've referred {referralCount} {referralCount === 1 ? "friend" : "friends"}
            </span>
          )}
        </p>
      )}

      <Button
        variant="outline"
        className="w-full flex items-center justify-center gap-2"
        onClick={handleShare}
        disabled={isProfileLoading}
      >
        <Share2 className="w-4 h-4" />
        {isGuest ? "Sign in to Refer" : "Share Your Link"}
      </Button>
    </div>
  );
};

export default ReferFriendCard;
